import { AddressWithoutId, FileUploadStatus, UpdateAction } from '@types';
import {
	array,
	boolean,
	InferType,
	mixed,
	MixedSchema,
	number,
	object,
	ObjectSchema,
	string,
} from 'yup';

// Mime for File Upload Schema
export const fileUploadMimeTypes: { [key: string]: string[] } = {
	image: ['image/jpeg', 'image/jpg', 'image/png', 'image/heif'],
	document: [
		'text/plain',
		'application/pdf',
		'application/msword',
		'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
	],
	all: [
		'image/jpeg',
		'image/jpg',
		'image/png',
		'image/heif',
		'text/plain',
		'application/pdf',
		'application/msword',
		'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
	],
};

// Mime to File Type
export const mimeTypeToFileType: { [key: string]: string } = {
	'image/jpeg': 'jpeg',
	'image/jpg': 'jpg',
	'image/png': 'png',
	'image/heif': 'heif',
	'text/plain': 'txt',
	'application/pdf': 'pdf',
	'application/msword': 'doc',
	'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
};

export const AddressFormValuesSchema = (
	generalErrorMessage?: string,
): ObjectSchema<AddressWithoutId> =>
	object()
		.shape({
			addressLine1: string().required(),
			addressLine2: string().notRequired(),
			suburb: string().required(),
			postalCode: string().required(),
			state: string().required(),
			country: string().required(),
			isOverwrittingPostalAddress: boolean().notRequired(),
		})
		.test('is-schema-valid', generalErrorMessage ?? '', function (value) {
			if (generalErrorMessage) {
				// return !!value.postalCode;
				return (
					!!value.addressLine1 &&
					// !!value.addressLine2 &&	// not required
					!!value.suburb &&
					!!value.postalCode &&
					!!value.state &&
					!!value.country
				);
			}
		});

// New schema for individual file
export const FileValidationSchema = (
	acceptedFileTypes: 'image' | 'document' | 'all',
): MixedSchema<File> => {
	return mixed<File>()
		.defined()
		.test(
			'isValidFileType',
			`Accepted file formats: ${fileUploadMimeTypes[acceptedFileTypes]
				.map(type => mimeTypeToFileType[type])
				.join(', ')}`,
			(file: File) => {
				if (!file) return true; // Allow empty value
				const validFileTypes = fileUploadMimeTypes[acceptedFileTypes];
				return validFileTypes.includes(file.type);
			},
		)
		.test('isValidFileSize', `File size is greater than 20MB`, (file: File) => {
			if (!file) return true; // Allow empty value
			const fileSizeInMB = file.size / (1024 * 1024);
			return fileSizeInMB <= 20;
		});
};

export const SingleFileSchema = (acceptedFileTypes: 'image' | 'document' | 'all') => {
	return object({
		id: string().notRequired().nonNullable(),
		action: mixed<UpdateAction>().notRequired().nonNullable(),
		file: FileValidationSchema(acceptedFileTypes).notRequired().nonNullable(),
		filename: string().notRequired().nonNullable(),
		fileLocation: object({
			application: string().nullable(),
			identifier: string().nullable(),
		})
			.notRequired()
			.nonNullable(),
		subject: number().notRequired().nonNullable(),
		number: number().notRequired().nonNullable(),
		base64: string().notRequired().nonNullable(),
		uploadStatus: object({
			status: mixed<FileUploadStatus>().required(),
			fileuri: string().nullable(),
			contactid: string().nullable(),
		})
			.notRequired()
			.nonNullable(),
		error: string().notRequired().nonNullable(),
	})
		.test('file-or-status', 'Please provide a file to upload', value => {
			const hasFile = value.file !== undefined;
			const hasUploadStatus = value.uploadStatus !== undefined;
			return hasFile || hasUploadStatus;
		})
		.test('file-validation', '', function (value) {
			let error = '';
			if (value.file) {
				const validFileTypes = fileUploadMimeTypes[acceptedFileTypes];
				if (!validFileTypes.includes(value.file.type)) {
					error = `Invalid file format`;
				}

				const fileSizeInMB = value.file.size / (1024 * 1024);
				if (fileSizeInMB > 20) {
					error = `File size is greater than 20MB`;
				}
			}
			return error === '' ? true : this.createError({ message: error });
		});
};

export type FileUploadItem = InferType<ReturnType<typeof SingleFileSchema>>;

export const FileUploadSchema = (
	acceptedFileTypes: 'image' | 'document' | 'all',
	requiredErrorMessage: string,
	minFiles = 1,
	maxFiles = 10,
) => {
	return array()
		.of(SingleFileSchema(acceptedFileTypes))
		.min(minFiles, requiredErrorMessage)
		.max(maxFiles, `You can upload up to ${maxFiles} files`)
		.required(requiredErrorMessage)
		.test('at-least-one-valid-file', requiredErrorMessage, value => {
			if (!value) return false;
			return value.some(
				file =>
					(file.uploadStatus?.status === FileUploadStatus.CLEAN ||
						file.uploadStatus?.status === FileUploadStatus.RESTORED) &&
					file.action !== UpdateAction.REMOVE,
			);
		})
		.test('no-invalid-status', 'There was an error uploading one or more files', value => {
			if (!value) return true;
			return !value.some(
				file =>
					// status is undefined for files that have validation errors and weren't uploaded
					file.uploadStatus?.status !== undefined &&
					file.uploadStatus.status !== FileUploadStatus.CLEAN &&
					file.uploadStatus.status !== FileUploadStatus.RESTORED &&
					// requested is not an error - submit button is disabled to prevent submission of requested status files
					file.uploadStatus.status !== FileUploadStatus.REQUESTED,
			);
		});
};

export const FileUploadOptionalSchema = (
	acceptedFileTypes: 'image' | 'document' | 'all',
	maxFiles = 10,
) => {
	return array()
		.of(SingleFileSchema(acceptedFileTypes))
		.max(maxFiles, `You can upload up to ${maxFiles} files`)
		.test('no-invalid-status', 'There was an error uploading one or more files', value => {
			if (!value) return true;
			return !value.some(
				file =>
					// status is undefined for files that have validation errors and weren't uploaded
					file.uploadStatus?.status !== undefined &&
					file.uploadStatus.status !== FileUploadStatus.CLEAN &&
					file.uploadStatus.status !== FileUploadStatus.RESTORED &&
					// requested is not an error - submit button is disabled to prevent submission of requested status files
					file.uploadStatus.status !== FileUploadStatus.REQUESTED,
			);
		});
};
